"use client";
import { couponActions } from "@/store/actions/coupon.action";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { useEffect, useState, useRef } from "react";
import { searchCoupons } from "@/services/couponService";
import { columns } from "@/components/coupons/columns";
import CreateDiscounts from "@/components/coupons/CreateDiscounts";
import { DataView } from "@/components/common/DataView";
import { FilterConfig, ActionButton } from "@/components/common/DataViewTypes";
import { FilterModal } from "@/components/common/FilterModal";
import { COUPON_FILTER_CONFIG } from "@/config/filters";
import { useCouponFilters } from "@/hooks/useCouponFilters";
import { useNavigate, useParams } from "react-router-dom";
import { Coupon } from "@/types/coupon";
import { getCouponById } from "@/services/discountService";
import {
  updateAmountOffOrder,
  updateAmountOffProducts,
  updateBuyXGetY,
  updateFreeShipping,
  setCurrentTab,
} from "@/store/slices/discount.slice";
import {
  AmountOffOrder,
  AmountOffProducts,
  BuyXGetY,
  FreeShipping,
} from "@/types/coupon/discount";
import { RouteGuard } from "@/components/common/RouteGuard";
import { Permission } from "@/lib/rbac";
import { Plus, RefreshCw } from "lucide-react";
import { convertDecimalToCurrency } from "@/utils/currency";

const getRouteFromCouponType = (type: string): string => {
  const typeToRouteMap: { [key: string]: string } = {
    AmountOffProduct: "amount-off-products",
    AmountOffOrder: "amount-off-order",
    BuyXGetY: "buy-x-get-y",
    FreeShipping: "free-shipping",
  };
  return typeToRouteMap[type] || type;
};

const Coupons = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { storeType, storeId } = useParams<{ storeType: string; storeId: string }>();
  const createDiscountsRef = useRef<HTMLButtonElement>(null);
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [searchResult, setSearchResult] = useState<any>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isFilterPanelExpanded, setIsFilterPanelExpanded] = useState(false);
  const { filters, updateFilters, getActiveFilterCount } = useCouponFilters();
  const pageSize = 5;

  const { list, pagination, loading, error } = useAppSelector(
    (state) => state.coupon,
  );

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(handler);
  }, [search]);

  // Fetch coupons (search or default with filters)
  useEffect(() => {
    if (debouncedSearch) {
      setSearchLoading(true);
      searchCoupons({
        page: pageIndex + 1,
        limit: pageSize,
        query: debouncedSearch,
      })
        .then((res) => {
          setSearchResult(res);
        })
        .finally(() => setSearchLoading(false));
    } else {
      setSearchResult(null);
      // Combine search query with filters
      const combinedFilters = { ...filters };
      if (search.trim()) {
        combinedFilters.title = search.trim();
      }

      // Only fetch if we don't have data OR if filters/page changed
      if ((list?.length || 0) === 0 || pageIndex > 0 || Object.keys(combinedFilters).length > 0) {
        dispatch(
          couponActions.fetchCoupons({
            page: pageIndex + 1,
            page_size: pageSize,
            filters: combinedFilters,
          }),
        );
      }
    }
  }, [debouncedSearch, pageIndex, dispatch, filters, search, list?.length]);

  const handleFiltersChange = (newFilters: any) => {
    updateFilters(newFilters);
    setPageIndex(0); // Reset to first page when filters change
  };

  const handleSearch = (query: string) => {
    setSearch(query);
    setPageIndex(0);
  };

  const handlePageChange = (newPageIndex: number) => {
    setPageIndex(newPageIndex);
  };

  const handleRowClick = async (rowData: Coupon) => {
    try {
      const response = await getCouponById(rowData.id);
      const couponData: any = response.data;
      const responseWithEntities: any = response; // Type assertion for entities
      console.log("Editing Coupon - Raw API Data:", couponData);
      console.log("Response with entities:", responseWithEntities);

      // Set the current tab based on automatic field
      if (couponData.automatic !== undefined) {
        const tabType = couponData.automatic ? "automatic" : "code";
        console.log("Setting current tab to:", tabType);
        dispatch(setCurrentTab(tabType));
      }

      // Helper function to convert timestamp to Date
      const timestampToDate = (
        timestamp: number | null | undefined,
      ): Date | undefined => {
        if (!timestamp) return undefined;
        return new Date(timestamp);
      };

      // Helper function to transform products from API format to SelectedProduct format
      const transformProducts = (products: any[]): any[] => {
        if (!Array.isArray(products)) return [];
        const transformed = products.map((product) => ({
          id: product.id,
          title: product.title || product.name,
          totalVariants: product.totalVariants || product.variants?.length || 0,
          variants: product.variants?.map((v: any) => ({ id: v.id })) || [],
        }));
        console.log("Transformed products:", transformed);
        return transformed;
      };

      // Helper function to transform collections from API format to Collection format
      const transformCollections = (collections: any[]): any[] => {
        if (!Array.isArray(collections)) return [];
        const transformed = collections.map((collection) => ({
          id: collection.id,
          title: collection.title || collection.name,
          products: collection.products || [],
        }));
        console.log("Transformed collections:", transformed);
        return transformed;
      };

      // Helper function to extract products/collections from entities
      const extractEntities = (entities: any[]): { products: any[], collections: any[] } => {
        if (!Array.isArray(entities) || entities.length === 0) {
          return { products: [], collections: [] };
        }
        const entity = entities[0] || {};
        return {
          products: entity.products || [],
          collections: entity.collections || [],
        };
      };

      // Extract entities from response
      const { products: entityProducts, collections: entityCollections } = extractEntities(responseWithEntities.entities);
      console.log("Extracted entities - products:", entityProducts, "collections:", entityCollections);

      // Base transformation data (convert paise to rupees for display)
      const baseTransformedData = {
        couponId: couponData.id,
        title: couponData.title || "",
        discount_code: couponData.discount_code || "",
        discount_type: couponData.discount_type || "percentage",
        discount_value_fixed:
          couponData.discount_type === "fixed"
            ? String(convertDecimalToCurrency(couponData.discount_value || 0))
            : "0",
        discount_value_percentage:
          couponData.discount_type === "percentage"
            ? String(couponData.discount_value || "0")
            : "0",
        discount_value_max: String(convertDecimalToCurrency(couponData.discount_value_max || 0)),
        minimum_purchase_type: couponData.minimum_purchase_type || "nothing",
        minimum_purchase_value_amount:
          couponData.minimum_purchase_type === "amount"
            ? String(convertDecimalToCurrency(couponData.minimum_purchase_value || 0))
            : "0",
        minimum_purchase_value_quantity:
          couponData.minimum_purchase_type === "quantity"
            ? String(couponData.minimum_purchase_value || "0")
            : "0",
        start_date: timestampToDate(couponData.start_date),
        end_date: timestampToDate(couponData.end_date),
      };

      console.log("Base transformed data:", baseTransformedData);

      const route = getRouteFromCouponType(rowData.type);
      console.log("Coupon type route:", route);

      switch (route) {
        case "amount-off-order": {
          const transformedData = {
            ...baseTransformedData,
          };
          console.log("Amount Off Order transformed data:", transformedData);
          dispatch(updateAmountOffOrder(transformedData as AmountOffOrder));
          break;
        }
        case "amount-off-products": {
          const transformedData = {
            ...baseTransformedData,
            applicable_products_type:
              couponData.applicable_products_type || "specific",
            applicable_products: transformProducts(entityProducts),
            applicable_collections: transformCollections(entityCollections),
          };
          console.log("Amount Off Products transformed data:", transformedData);
          dispatch(
            updateAmountOffProducts(transformedData as AmountOffProducts),
          );
          break;
        }
        case "buy-x-get-y": {
          // For buy-x-get-y, we need separate entities for buy_x and get_y
          const buyXEntities = extractEntities(responseWithEntities.buy_x_entities || []);
          const getYEntities = extractEntities(responseWithEntities.get_y_entities || []);
          const transformedData = {
            ...baseTransformedData,
            applicable_products_type:
              couponData.applicable_products_type || "specific",
            applicable_x_products: transformProducts(buyXEntities.products),
            applicable_x_collections: transformCollections(buyXEntities.collections),
            applicable_y_products: transformProducts(getYEntities.products),
            applicable_y_collections: transformCollections(getYEntities.collections),
            get_y_quantity: couponData.get_y_quantity || 0,
          };
          console.log("Buy X Get Y transformed data:", transformedData);
          dispatch(updateBuyXGetY(transformedData as BuyXGetY));
          break;
        }
        case "free-shipping": {
          const transformedData = {
            ...baseTransformedData,
          };
          console.log("Free Shipping transformed data:", transformedData);
          dispatch(updateFreeShipping(transformedData as FreeShipping));
          break;
        }
      }
      console.log("Navigating to:", `/${storeType}/${storeId}/coupons/${route}`);
      navigate(`/${storeType}/${storeId}/coupons/${route}`);
    } catch (error) {
      console.error("Failed to fetch coupon details:", error);
    }
  };

  // Configure filter panel
  const filterConfig: FilterConfig = {
    component: (
      <FilterModal
        isOpen={isFilterPanelExpanded && !debouncedSearch}
        onClose={() => setIsFilterPanelExpanded(false)}
        onApplyFilters={handleFiltersChange}
        config={COUPON_FILTER_CONFIG}
        initialFilters={filters}
      />
    ),
    isOpen: isFilterPanelExpanded && !debouncedSearch,
    onToggle: () => setIsFilterPanelExpanded(!isFilterPanelExpanded),
    activeCount: getActiveFilterCount(),
  };

  // Handle refresh - directly dispatch action to force a fresh fetch
  const handleRefresh = () => {
    if (debouncedSearch) {
      setSearchLoading(true);
      searchCoupons({
        page: pageIndex + 1,
        limit: pageSize,
        query: debouncedSearch,
      })
        .then((res) => {
          setSearchResult(res);
        })
        .finally(() => setSearchLoading(false));
    } else {
      const combinedFilters = { ...filters };
      if (search.trim()) {
        combinedFilters.title = search.trim();
      }
      dispatch(
        couponActions.fetchCoupons({
          page: pageIndex + 1,
          page_size: pageSize,
          filters: combinedFilters,
        }),
      );
    }
  };

  // Determine current data and pagination
  const currentData = searchResult ? searchResult.data : list;
  const currentPagination = searchResult ? searchResult.pagination : pagination;
  const currentLoading = searchResult ? searchLoading : loading;

  // Configure action buttons
  const actionButtons: ActionButton[] = [
    {
      label: "Refresh",
      icon: <RefreshCw className={`w-3 h-3 ${currentLoading ? 'animate-spin' : ''}`} />,
      onClick: handleRefresh,
      variant: "outline",
      disabled: currentLoading,
    },
    {
      label: "Create Discount",
      icon: <Plus className="w-3 h-3" />,
      onClick: () => {
        createDiscountsRef.current?.click();
      },
      variant: "primary",
    },
  ];

  return (
    <RouteGuard
      allowedStoreTypes={["seller"]}
      requiredPermissions={[Permission.VIEW_COUPONS]}
    >
      <div className="space-y-4">
        <div className="bg-secondary-background p-[18px] border rounded-[16px]">
          <DataView
            columns={columns({ onRowClick: handleRowClick })}
            data={currentData || []}
            pagination={currentPagination}
            pageIndex={pageIndex}
            onPageChange={handlePageChange}
            loading={currentLoading}
            error={error}
            title="Coupons"
            description="Create and manage discount codes and automatic discounts for your store"
            searchPlaceholder="Search coupons..."
            searchQuery={search}
            onSearch={handleSearch}
            filterConfig={filterConfig}
            actionButtons={actionButtons}
            emptyStateType="coupons"
          />
          {/* Keep CreateDiscounts component for its modal functionality */}
          <div
            style={{
              position: "absolute",
              visibility: "hidden",
              pointerEvents: "none",
            }}
          >
            <CreateDiscounts ref={createDiscountsRef} />
          </div>
        </div>
      </div>
    </RouteGuard>
  );
};

export default Coupons;
